/**
 * Hazard Rules Knowledge Base
 * 
 * Maps ingredient keywords to hormone-axis impacts.
 * Reframed to focus on signals and drivers rather than medical diagnosis.
 */

const hazardRules = [
    // --- Phthalates ---
    {
        id: 'phthalate-fragrance',
        matchPatterns: [/fragrance/i, /parfum/i, /perfume/i],
        category: 'Fragrance',
        impacts: { reproductive: -1, skin: -1 },
        confidence: 'low',
        evidenceType: 'proxy',
        shortReason: 'Contains fragrance',
        detailReason: 'Fragrance can sometimes indicate undisclosed compounds (like phthalates), but this is not a direct confirmation.',
        exposureNote: 'Common in personal care, cleaning products',
    },
    {
        id: 'phthalate-dehp',
        matchPatterns: [/DEHP/i, /di\s*\(2-ethylhexyl\)\s*phthalate/i, /diethylhexyl phthalate/i],
        category: 'Phthalates (DEHP)',
        impacts: { reproductive: -5, thyroid: -2 },
        confidence: 'high',
        evidenceType: 'direct',
        shortReason: 'Contains DEHP phthalate',
        detailReason: 'Direct match for DEHP, which has anti-androgenic and thyroid-related signals in studies.',
        exposureNote: 'Food contact, PVC products, medical devices',
    },
    {
        id: 'phthalate-dbp',
        matchPatterns: [/DBP/i, /dibutyl phthalate/i],
        category: 'Phthalates (DBP)',
        impacts: { reproductive: -4 },
        confidence: 'high',
        evidenceType: 'direct',
        shortReason: 'Contains DBP phthalate',
        detailReason: 'Direct match for DBP, which shows anti-androgenic signals.',
        exposureNote: 'Nail polish, adhesives, some personal care',
    },
    {
        id: 'phthalate-dep',
        matchPatterns: [/DEP/i, /diethyl phthalate/i],
        category: 'Phthalates (DEP)',
        impacts: { reproductive: -3 },
        confidence: 'moderate',
        evidenceType: 'direct',
        shortReason: 'Contains DEP phthalate',
        detailReason: 'Direct match for DEP, which has weaker anti-androgenic signals than other phthalates.',
        exposureNote: 'Fragrance carrier in cosmetics',
    },

    // --- Bisphenols ---
    {
        id: 'bpa',
        matchPatterns: [/\bBPA\b/i, /bisphenol\s*A/i],
        category: 'Bisphenols (BPA)',
        impacts: { reproductive: -3, thyroid: -3 },
        confidence: 'high',
        evidenceType: 'direct',
        shortReason: 'Contains BPA',
        detailReason: 'Direct match for BPA, which has estrogen-receptor interactions and thyroid-related signals.',
        exposureNote: 'Food-contact plastics, can liners, thermal paper',
    },
    {
        id: 'bps-bpf',
        matchPatterns: [/\bBPS\b/i, /\bBPF\b/i, /bisphenol\s*S/i, /bisphenol\s*F/i],
        category: 'Bisphenols (BPS/BPF)',
        impacts: { reproductive: -2, thyroid: -2 },
        confidence: 'moderate',
        evidenceType: 'direct',
        shortReason: 'Contains BPA alternatives (BPS/BPF)',
        detailReason: 'Direct match for BPS/BPF, which are marketed as BPA-free but show similar receptor activity.',
        exposureNote: '"BPA-free" products may still contain these analogs',
    },

    // --- PFAS ---
    {
        id: 'pfas-general',
        matchPatterns: [/\bPFAS\b/i, /\bPFOS\b/i, /\bPFOA\b/i, /perfluoro/i, /polyfluoro/i],
        category: 'PFAS',
        impacts: { thyroid: -4, metabolic: -2 },
        confidence: 'high',
        evidenceType: 'direct',
        shortReason: 'Contains PFAS compounds',
        detailReason: 'Direct match for PFAS, persistent chemicals with immune and metabolic signals.',
        exposureNote: 'Nonstick cookware, stain-resistant fabrics',
    },
    {
        id: 'pfas-nonstick',
        matchPatterns: [/nonstick/i, /non-stick/i, /teflon/i, /PTFE/i],
        category: 'PFAS (nonstick proxy)',
        impacts: { thyroid: -2 },
        confidence: 'low',
        evidenceType: 'proxy',
        shortReason: 'Marketed as nonstick',
        detailReason: 'This is a proxy term. Some nonstick products use PFAS, depending on formulation and age.',
        exposureNote: 'Cookware, bakeware — exposure varies by product age',
    },
    {
        id: 'pfas-stain-resistant',
        matchPatterns: [/stain.?resist/i, /water.?repell/i, /scotchgard/i],
        category: 'PFAS (stain-resistant proxy)',
        impacts: { thyroid: -1 },
        confidence: 'low',
        evidenceType: 'proxy',
        shortReason: 'Marketed as stain or water resistant',
        detailReason: 'This is a proxy term. Stain and water resistance can sometimes indicate PFAS treatments.',
        exposureNote: 'Furniture, textiles, outdoor gear',
    },

    // --- Parabens ---
    {
        id: 'paraben-strong',
        matchPatterns: [/propylparaben/i, /butylparaben/i, /isobutylparaben/i],
        category: 'Parabens (propyl/butyl)',
        impacts: { reproductive: -2 },
        confidence: 'high',
        evidenceType: 'direct',
        shortReason: 'Contains long-chain parabens',
        detailReason: 'Direct match for propyl- or butylparaben, which show estrogenic signals.',
        exposureNote: 'Leave-on lotions, cosmetics',
    },
    {
        id: 'paraben-weak',
        matchPatterns: [/methylparaben/i, /ethylparaben/i],
        category: 'Parabens (methyl/ethyl)',
        impacts: { reproductive: -1 },
        confidence: 'moderate',
        evidenceType: 'direct',
        shortReason: 'Contains short-chain parabens',
        detailReason: 'Direct match for methyl/ethylparaben, which have very weak estrogenic signals and rapid metabolism.',
        exposureNote: 'Widely used preservative in cosmetics',
    },

    // --- Triclosan ---
    {
        id: 'triclosan',
        matchPatterns: [/triclosan/i, /triclocarban/i],
        category: 'Antimicrobials (triclosan)',
        impacts: { thyroid: -3 },
        confidence: 'high',
        evidenceType: 'direct',
        shortReason: 'Contains triclosan',
        detailReason: 'Direct match for triclosan, an antimicrobial with thyroid pathway signals.',
        exposureNote: 'Some antibacterial soaps, toothpaste (regulations vary)',
    },

    // --- UV Filters ---
    {
        id: 'oxybenzone',
        matchPatterns: [/oxybenzone/i, /benzophenone-3/i],
        category: 'UV Filters (oxybenzone)',
        impacts: { reproductive: -2, thyroid: -2 },
        confidence: 'moderate',
        evidenceType: 'direct',
        shortReason: 'Contains oxybenzone',
        detailReason: 'UV filter with reproductive and thyroid signals in epidemiological studies (reduced testosterone, altered thyroid in pregnancy), but thyroid effects are inconsistent across controlled studies. No causal human evidence established.',
        exposureNote: 'Sunscreens; high use patterns can raise biomarkers',
    },
    {
        id: 'octinoxate',
        matchPatterns: [/octinoxate/i, /octyl methoxycinnamate/i, /ethylhexyl methoxycinnamate/i],
        category: 'UV Filters (octinoxate)',
        impacts: { reproductive: -1, thyroid: -1 },
        confidence: 'moderate',
        evidenceType: 'direct',
        shortReason: 'Contains octinoxate',
        detailReason: 'UV filter with weak estrogenic activity in experimental systems.',
        exposureNote: 'Sunscreens, moisturizers with SPF',
    },
    {
        id: 'homosalate',
        matchPatterns: [/homosalate/i],
        category: 'UV Filters (homosalate)',
        impacts: { reproductive: -1 },
        confidence: 'low',
        evidenceType: 'direct',
        shortReason: 'Contains homosalate',
        detailReason: 'UV filter with potential hormonal activity currently under review.',
        exposureNote: 'Sunscreens',
    },

    // --- Pesticides ---
    {
        id: 'organochlorine',
        matchPatterns: [/organochlorine/i, /\bDDT\b/i, /\bDDE\b/i, /lindane/i, /chlordane/i],
        category: 'Pesticides (legacy)',
        impacts: { reproductive: -3, thyroid: -2 },
        confidence: 'high',
        evidenceType: 'direct',
        shortReason: 'Contains organochlorines',
        detailReason: 'Direct match for legacy pesticides with known receptor effects.',
        exposureNote: 'Legacy chemicals; persistent in environment',
    },
    {
        id: 'glyphosate',
        matchPatterns: [/glyphosate/i, /roundup/i],
        category: 'Pesticides (glyphosate)',
        impacts: { reproductive: -1 },
        confidence: 'low',
        evidenceType: 'direct',
        shortReason: 'Contains glyphosate',
        detailReason: 'Some endocrine signals observed in experimental studies, though debated.',
        exposureNote: 'Dietary residues; occupational exposure higher',
    },

    // --- Flame retardants ---
    {
        id: 'flame-retardant',
        matchPatterns: [/\bPBDE\b/i, /polybrominated/i, /brominated/i],
        category: 'Flame Retardants (PBDEs)',
        impacts: { thyroid: -4 },
        confidence: 'high',
        evidenceType: 'direct',
        shortReason: 'Contains brominated flame retardants',
        detailReason: 'Direct indications of PBDEs, which have thyroid hormone transport interference signals.',
        exposureNote: 'Indoor dust; furniture/foam; legacy exposures persist',
    },
    {
        id: 'flame-retardant-proxy',
        matchPatterns: [/flame.?retard/i],
        category: 'Flame Retardants (proxy)',
        impacts: { thyroid: -2 },
        confidence: 'low',
        evidenceType: 'proxy',
        shortReason: 'Marketed as flame retardant',
        detailReason: 'Proxy term for potential flame retardants. Not all modern retardants use PBDEs.',
        exposureNote: 'Indoor dust; furniture/foam',
    },

    // --- PVC / Vinyl ---
    {
        id: 'pvc',
        matchPatterns: [/\bPVC\b/i, /\bvinyl\b/i, /polyvinyl chloride/i],
        category: 'PVC/Vinyl (phthalate proxy)',
        impacts: { reproductive: -1 },
        confidence: 'low',
        evidenceType: 'proxy',
        shortReason: 'Contains PVC or Vinyl',
        detailReason: 'Proxy signal. Flexible vinyl often contains phthalate plasticizers, though not explicitly listed.',
        exposureNote: 'Shower curtains, food wrap, flooring',
    },

    // --- Antibacterial products ---
    {
        id: 'antibacterial-general',
        matchPatterns: [/antibacterial/i, /antimicrobial/i],
        category: 'Antibacterial products',
        impacts: { skin: -2 },
        confidence: 'low',
        evidenceType: 'proxy',
        shortReason: 'Marketed as antibacterial',
        detailReason: 'Proxy term. Broad-spectrum antibacterials can disrupt the skin microbiome, potentially worsening acne or eczema over time.',
        exposureNote: 'Hand soaps, cleaning sprays',
    },

    // --- Formaldehyde ---
    {
        id: 'formaldehyde',
        matchPatterns: [/formaldehyde/i, /DMDM hydantoin/i, /diazolidinyl urea/i, /imidazolidinyl urea/i, /quaternium-15/i],
        category: 'Formaldehyde releasers',
        impacts: { metabolic: -1 },
        confidence: 'moderate',
        evidenceType: 'direct',
        shortReason: 'Contains formaldehyde releasers',
        detailReason: 'Direct match for preservatives that release formaldehyde; some endocrine signals in high exposure contexts.',
        exposureNote: 'Hair products, nail polish, some lotions',
    },

    // --- SLS/SLES ---
    {
        id: 'sls',
        matchPatterns: [/sodium lauryl sulfate/i, /sodium laureth sulfate/i, /\bSLS\b/, /\bSLES\b/],
        category: 'Surfactants (SLS/SLES)',
        impacts: { skin: -2 },
        confidence: 'moderate',
        evidenceType: 'direct',
        shortReason: 'Contains SLS or SLES',
        detailReason: 'Disrupts skin microbiome by stripping lipids and elevating pH, promoting C. acnes and S. aureus proliferation; barrier dysbiosis pathway to acne and irritation.',
        exposureNote: 'Shampoos, body washes, toothpaste',
    },
    // --- Comedogenic Ingredients ---
    {
        id: 'comedogenic-isopropyl-esters',
        matchPatterns: [/isopropyl palmitate/i, /isopropyl myristate/i],
        category: 'Comedogenic esters',
        impacts: { skin: -3 },
        confidence: 'moderate',
        evidenceType: 'direct',
        shortReason: 'Contains pore-clogging esters',
        detailReason: 'Isopropyl palmitate/myristate rated 4-5 on comedogenic scale; common in foundations and moisturizers.',
        exposureNote: 'Risk scales with concentration and position in ingredient list',
    },
    {
        id: 'comedogenic-lanolin',
        matchPatterns: [/acetylated lanolin/i, /lanolin alcohol/i],
        category: 'Comedogenic lanolin',
        impacts: { skin: -3 },
        confidence: 'moderate',
        evidenceType: 'direct',
        shortReason: 'Contains comedogenic lanolin derivatives',
        detailReason: 'Acetylated lanolin and lanolin alcohol are highly comedogenic (rating 4-5) and can exacerbate acne.',
        exposureNote: 'Lotions, lip products',
    },
    {
        id: 'comedogenic-cocoa-butter',
        matchPatterns: [/cocoa butter/i, /theobroma cacao seed butter/i],
        category: 'Comedogenic butters',
        impacts: { skin: -2 },
        confidence: 'moderate',
        evidenceType: 'direct',
        shortReason: 'Contains cocoa butter',
        detailReason: 'Rated 4 on the comedogenic scale; can clog pores especially in leave-on facial products.',
        exposureNote: 'Body lotions, lip balms',
    },
    {
        id: 'comedogenic-coconut-oils',
        matchPatterns: [/coconut oil/i, /cocos nucifera oil/i],
        category: 'Comedogenic oils',
        impacts: { skin: -2 },
        confidence: 'moderate',
        evidenceType: 'direct',
        shortReason: 'Contains coconut oil',
        detailReason: 'Unrefined coconut oil is highly comedogenic (rating 4) for acne-prone skin.',
        exposureNote: 'DIY skincare, natural products',
    },
    {
        id: 'comedogenic-algae',
        matchPatterns: [/algae extract/i, /laminaria/i, /macrocystis/i],
        category: 'Comedogenic extracts',
        impacts: { skin: -3 },
        confidence: 'moderate',
        evidenceType: 'direct',
        shortReason: 'Contains algae extract',
        detailReason: 'Algae extracts are highly comedogenic (rating 5) and known to trigger breakouts.',
        exposureNote: 'Anti-aging serums, marine skincare',
    },
    {
        id: 'comedogenic-wheat-germ',
        matchPatterns: [/wheat germ oil/i, /triticum vulgare germ oil/i],
        category: 'Comedogenic oils',
        impacts: { skin: -3 },
        confidence: 'moderate',
        evidenceType: 'direct',
        shortReason: 'Contains wheat germ oil',
        detailReason: 'Wheat germ oil is rated 5 on the comedogenic scale.',
        exposureNote: 'Natural/organic moisturizers',
    },
];

export default hazardRules;
